import React from 'react';
import PropTypes from 'prop-types';
import { intlShape } from 'react-intl';
import classnames from 'classnames';
import Price from '../price/PriceComponent';
import ProgressiveButton from '../../common/progressive-button/ProgressiveButtonComponent';
import PaperPicker from '../paper-picker/PaperPickerComponent';
import DropdownSelect from '../../common/dropdown-select/DropdownSelectComponent';
import ProductRename from '../rename/RenameComponent';
import ProductImage from '../image/ProductImageComponent';
import { APPROVAL_STATUS } from '../../../api/types/Approvals';

import { canUpdateProduct } from '../../services/user-permission/UserPermissionService';

import messages from '../../../data/products';

import ProductUnavailable from './ProductUnavailable';
import './Product.scss';

/**
 * Displays an MBS Product
 */

class ProductComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      deleteClass: props.data.isDeleted ? 'product-component--delete product-component--deleted' : '',
    };
    this.updateQuantity = this.updateQuantity.bind(this);
    this.addToCart = this.addToCart.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps: { data: { isAdded: boolean } }) {
    if (!this.props.data.isAdded && nextProps.data.isAdded) {
      setTimeout(() => {
        this.props.actions.productChange(this.props.data.id, { isAdded: false });
      }, 1000);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data.isDeleted !== this.props.data.isDeleted) {
      this.deleteClass();
    }
  }

  updateQuantity(input) {
    const newQuantity = input.value;
    const { quantity, availability, id, projectId } = this.props.data;
    if (availability.isAvailable && newQuantity !== quantity) {
      this.props.actions.quantityUpdate(id, projectId, newQuantity, this.props.runtimeConfig.api);
    }
  }

  addToCart(): void {
    const { isAdding, isAdded, availability, id, approvalStatus } = this.props.data;
    if (availability.isAvailable && !isAdding && !isAdded) {
      this.props.actions.productChange(id, { isAdding: true });
      this.props.actions.cartRequest({ ids: [id], approvalStatus });
    }
  }

  quantityOptions() {
    const { quantities, quantity } = this.props.data;
    return quantities.map((quantityItem: number) => ({
      value: `${quantityItem}`,
      text: `${quantityItem}`,
      active: quantityItem === quantity,
    }));
  }

  hasDisabledCTAs() {
    const { data, disableCTAs } = this.props;
    return disableCTAs || data.isDisabled || data.isChecked;
  }

  imageCapsuleData() {
    const { approvalStatus } = this.props.data;

    if (approvalStatus !== APPROVAL_STATUS.notApplicable && !this.props.isApprovalsView) {
      if (approvalStatus === APPROVAL_STATUS.pending) {
        return {
          text: this.context.intl.formatMessage(messages.productPendingApproval),
          tooltipText: this.context.intl.formatMessage(messages.productPendingApprovalTooltip),
          colour: 'yolk',
        };
      }

      if (approvalStatus === APPROVAL_STATUS.declined) {
        return {
          text: this.context.intl.formatMessage(messages.productDeclinedApproval),
          tooltipText: this.context.intl.formatMessage(messages.productDeclinedApprovalTooltip),
          colour: 'fox',
        };
      }

      if (approvalStatus === APPROVAL_STATUS.approved) {
        return {
          text: this.context.intl.formatMessage(messages.productApprovedApproval),
          tooltipText: this.context.intl.formatMessage(messages.productApprovedApprovalTooltip),
          colour: 'miami',
        };
      }

      return {
        text: this.context.intl.formatMessage(messages.productDraftApproval),
        tooltipText: this.context.intl.formatMessage(messages.productDraftApprovalTooltip),
        colour: 'ghost-dark',
      };
    }

    return {};
  }

  renderPaperPicker() {
    const { data } = this.props;
    return (
      <PaperPicker
        url={data.endpoints.paperOptionsUrl}
        isDisabled={this.hasDisabledCTAs()}
        isEditable={data.paperOptionRestricted === false}
        paperOption={data.paperOption}
        urlEdit={data.endpoints.pixelEditUrl}
      />
    );
  }

  renderQuantityDropdown() {
    const { quantity, quantities } = this.props.data;
    if (quantities.length <= 1) {
      return <span className="product-component__quantity">{quantity}</span>;
    }

    return (
      <DropdownSelect
        position="bottom-right"
        selectedValue={`${quantity}`}
        values={this.quantityOptions()}
        onChange={this.updateQuantity}
        disabled={this.hasDisabledCTAs()}
      />
    );
  }

  renderPurchasing() {
    const { data, disableCTAs } = this.props;

    const buttons = this.props.isApprovalsView ? (
      <div />
    ) : (
      <span className="product-component__button">
        <ProgressiveButton
          text={this.context.intl.formatMessage(messages.addToCartButton)}
          isDisabled={
            disableCTAs ||
            (data.approvalStatus !== APPROVAL_STATUS.approved && data.approvalStatus !== APPROVAL_STATUS.notApplicable)
          }
          onClick={this.addToCart}
          loading={data.isAdding && !data.isChecked}
          success={data.isAdded && !data.isChecked}
        />
      </span>
    );

    return (
      <div className="product-component__purchasing">
        <Price price={data.price} />
        {buttons}
      </div>
    );
  }

  deleteClass() {
    if (this.props.data.isDeleted) {
      this.setState({ deleteClass: 'product-component--delete' }, () => {
        setTimeout(() => {
          this.setState({
            deleteClass: 'product-component--delete product-component--deleted',
          });
        }, 600);
      });
    } else {
      this.setState({
        deleteClass: '',
      });
    }
  }

  renderPreview() {
    const { actions, data, disableCTAs, isApprovalsView, permissions } = this.props;
    const { text, colour, tooltipText } = this.imageCapsuleData();
    const hasBulkSelect = permissions.includes('bulkSelectProducts');

    return (
      <ProductImage
        data={data}
        actions={actions}
        disableCTAs={disableCTAs}
        permissions={permissions}
        disableSelection={isApprovalsView}
        hasBulkSelect={hasBulkSelect}
        capsuleText={text}
        capsuleColour={colour}
        tooltipText={tooltipText}
      />
    );
  }

  renderInfo() {
    return (
      <div className="product-component__content">
        <div className="product-component__info">
          {this.renderName()}
          {this.renderOptionsOrUnavailable()}
        </div>
      </div>
    );
  }

  renderName() {
    const { permissions } = this.props;
    const isDisabled = this.props.disableCTAs || !canUpdateProduct(permissions);
    return (
      <ProductRename
        content={this.props.data.name}
        actions={this.props.actions}
        productId={this.props.data.id}
        projectId={this.props.data.projectId}
        runtimeConfig={this.props.runtimeConfig}
        isDisabled={isDisabled}
      />
    );
  }

  renderOptionsOrUnavailable() {
    const { availability } = this.props.data;
    if (availability.isAvailable) {
      return this.renderOptions();
    }
    return <ProductUnavailable availability={availability} />;
  }

  renderOptions() {
    return (
      <div>
        {this.renderQuantityDropdown()}
        {this.renderPaperPicker()}
        {this.renderPurchasing()}
      </div>
    );
  }

  render() {
    const { id, isChecked, approvalStatus } = this.props.data;

    const containerClassNames = classnames('product-component__container', {
      'product-component__container--checked': isChecked,
      'product-component__container--dashed-border': approvalStatus === APPROVAL_STATUS.draft,
    });

    return (
      <div key={`product--${id}`} style={this.props.style} className={`product-component ${this.state.deleteClass}`}>
        <div className={containerClassNames}>
          {this.renderPreview()}
          {this.renderInfo()}
        </div>
      </div>
    );
  }
}

ProductComponent.displayName = 'Product';

ProductComponent.propTypes = {
  isApprovalsView: PropTypes.bool,
  disableCTAs: PropTypes.bool.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
  style: PropTypes.object,
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    lastUpdated: PropTypes.string.isRequired,
    paperOption: PropTypes.string.isRequired,
    paperOptionRestricted: PropTypes.bool.isRequired,
    availability: PropTypes.shape({
      isAvailable: PropTypes.bool.isRequired,
      availableInStores: PropTypes.array.isRequired,
    }).isRequired,
    approvalStatus: PropTypes.string,
    quantities: PropTypes.arrayOf(PropTypes.number).isRequired,
    quantity: PropTypes.number.isRequired,
    isAdding: PropTypes.bool,
    isAdded: PropTypes.bool,
    isDeleting: PropTypes.bool,
    isDeleted: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isChecked: PropTypes.bool,
    images: PropTypes.shape({
      thumb: PropTypes.string.isRequired,
    }).isRequired,
    productSize: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    endpoints: PropTypes.shape({
      downloadPdfUrl: PropTypes.string.isRequired,
      pixelEditUrl: PropTypes.string.isRequired,
      paperOptionsUrl: PropTypes.string,
    }).isRequired,
    price: PropTypes.shape({
      discounted: PropTypes.string.isRequired,
      original: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    cartRequest: PropTypes.func.isRequired,
    productChange: PropTypes.func.isRequired,
    quantityUpdate: PropTypes.func.isRequired,
  }),
  runtimeConfig: PropTypes.shape({
    api: PropTypes.string.isRequired,
  }),
};

ProductComponent.contextTypes = {
  intl: intlShape.isRequired,
};

export default ProductComponent;
